import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { imgUrl } from '../utils/api';

/* ── Styled ─────────────────────────────────────────────────── */
const Wrap = styled.div`
  display: flex; flex-direction: column; gap: 12px;
`;
const MainWrap = styled.div`
  border-radius: var(--radius-lg); overflow: hidden;
  background: var(--gray-100); position: relative;
  aspect-ratio: 1; cursor: crosshair;
  border: 1px solid var(--gray-100);
`;
const MainImg = styled.img`
  width: 100%; height: 100%; object-fit: cover;
  display: block; transition: opacity .2s;
`;
/* Zoom lens overlay */
const ZoomLens = styled.div`
  position: absolute; border: 1.5px solid var(--primary);
  background: rgba(45,106,79,.08); pointer-events: none;
  border-radius: 4px; display: ${p => p.$show ? 'block' : 'none'};
`;
/* Zoom result panel */
const ZoomResult = styled.div`
  position: absolute; top: 0; left: calc(100% + 12px);
  width: 380px; height: 380px; border-radius: var(--radius-lg);
  overflow: hidden; border: 1px solid var(--gray-200);
  box-shadow: var(--shadow-xl); background: #fff;
  display: ${p => p.$show ? 'block' : 'none'};
  background-image: url(${p => p.$src});
  background-repeat: no-repeat;
  z-index: 10;
  @media(max-width:1100px){ display: none !important; }
`;
const Thumbs = styled.div`
  display: flex; gap: 8px; overflow-x: auto; padding-bottom: 4px;
  &::-webkit-scrollbar { height: 4px; }
  &::-webkit-scrollbar-thumb { background: var(--gray-200); border-radius: 2px; }
`;
const Thumb = styled.button`
  width: 68px; height: 68px; border-radius: var(--radius);
  overflow: hidden; flex-shrink: 0; padding: 0;
  border: 2px solid ${p => p.$active ? 'var(--primary)' : 'transparent'};
  outline: 1px solid ${p => p.$active ? 'var(--primary)' : 'var(--gray-200)'};
  cursor: pointer; transition: var(--transition); background: var(--gray-100);
  img { width: 100%; height: 100%; object-fit: cover; display: block; }
  &:hover { border-color: var(--primary); }
`;
const NavBtn = styled.button`
  position: absolute; top: 50%; transform: translateY(-50%);
  ${p => p.$dir === 'prev' ? 'left: 10px' : 'right: 10px'};
  width: 34px; height: 34px; border-radius: 50%;
  background: rgba(255,255,255,.9); border: 1px solid var(--gray-200);
  box-shadow: var(--shadow-md); font-size: 1rem; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: var(--transition); z-index: 2;
  &:hover { background: #fff; color: var(--primary); }
`;
const Counter = styled.div`
  position: absolute; bottom: 10px; right: 10px;
  background: rgba(0,0,0,.45); color: #fff;
  font-size: 11px; padding: 3px 8px; border-radius: 100px;
`;
const ZoomHint = styled.div`
  position: absolute; bottom: 10px; left: 10px;
  background: rgba(0,0,0,.4); color: #fff;
  font-size: 11px; padding: 3px 8px; border-radius: 100px;
  pointer-events: none;
  @media(max-width:1100px){ display: none; }
`;

const LENS_SIZE = 120;

export default function ImageGallery({ images = [], name = '' }) {
  const [active, setActive] = useState(0);
  const [zoom, setZoom]     = useState(false);
  const [lensPos, setLensPos] = useState({ x: 0, y: 0 });
  const [bgPos, setBgPos]   = useState('0% 0%');
  const mainRef = useRef(null);

  // Clamp active index
  const imgs = images.length ? images : [''];
  const src  = imgUrl(imgs[active]);

  const prev = () => setActive(i => (i - 1 + imgs.length) % imgs.length);
  const next = () => setActive(i => (i + 1) % imgs.length);

  // Keyboard navigation
  useEffect(() => {
    const handler = e => {
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  const handleMouseMove = (e) => {
    const rect = mainRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Clamp lens within image
    const lx = Math.max(0, Math.min(x - LENS_SIZE / 2, rect.width  - LENS_SIZE));
    const ly = Math.max(0, Math.min(y - LENS_SIZE / 2, rect.height - LENS_SIZE));
    setLensPos({ x: lx, y: ly });

    // Background position for zoom result
    const pctX = (x / rect.width)  * 100;
    const pctY = (y / rect.height) * 100;
    setBgPos(`${pctX}% ${pctY}%`);
  };

  return (
    <Wrap>
      <MainWrap
        ref={mainRef}
        onMouseEnter={() => setZoom(true)}
        onMouseLeave={() => setZoom(false)}
        onMouseMove={handleMouseMove}
      >
        <MainImg src={src} alt={`${name} — view ${active + 1}`} />

        {/* Lens */}
        <ZoomLens
          $show={zoom}
          style={{
            width:  LENS_SIZE,
            height: LENS_SIZE,
            left:   lensPos.x,
            top:    lensPos.y,
          }}
        />

        {/* Zoom result — appears to the right on desktop */}
        <ZoomResult
          $show={zoom}
          $src={src}
          style={{
            backgroundSize:     '350%',
            backgroundPosition: bgPos,
          }}
        />

        {/* Prev / Next arrows (only when >1 image) */}
        {imgs.length > 1 && <>
          <NavBtn $dir="prev" onClick={prev}>‹</NavBtn>
          <NavBtn $dir="next" onClick={next}>›</NavBtn>
          <Counter>{active + 1} / {imgs.length}</Counter>
        </>}

        <ZoomHint>🔍 Hover to zoom</ZoomHint>
      </MainWrap>

      {/* Thumbnails */}
      {imgs.length > 1 && (
        <Thumbs>
          {imgs.map((img, i) => (
            <Thumb key={i} $active={active === i} onClick={() => setActive(i)}>
              <img src={imgUrl(img)} alt={`${name} ${i + 1}`} loading="lazy" />
            </Thumb>
          ))}
        </Thumbs>
      )}
    </Wrap>
  );
}
